import React, { useEffect, useRef, useState } from 'react';
import { getEvidenceUrl, getPrimaryEvidencePreview } from '../../../services/violations';
import { describeEvidenceState } from '../../../constants/violationConstants';
import { evidenceQueue, CANCELLED } from '../../../utils/evidenceQueue';

/**
 * PART 3, раздел 3: превью грузится лениво.
 *
 * Запрос стартует только когда строка попала во вьюпорт, и проходит через
 * общую очередь с лимитом 4 — иначе страница на 50 событий устроит бэкенду
 * N+1 обстрел. Ушедшая из вьюпорта строка снимает свою задачу, если та ещё не
 * стартовала.
 *
 * При ошибке загрузки картинки: если в превью есть evidenceId, один раз
 * запрашиваем свежую URL и повторяем. Вторая неудача → unavailable.
 * Флаг повтора сбрасывается при смене eventId.
 */
const ViolationEvidenceThumbnail = ({ eventId, caption = '', onOpen }) => {
  const holderRef = useRef(null);
  const retriedRef = useRef(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Сбрасываем флаг повтора при смене события, иначе для нового события не будет повтора.
    retriedRef.current = false;
  }, [eventId]);

  useEffect(() => {
    const node = holderRef.current;
    if (!node || !eventId) return undefined;

    let task = null;
    let cancelled = false;

    const load = () => {
      if (task) return;
      setLoading(true);
      task = evidenceQueue.enqueue(() => getPrimaryEvidencePreview(eventId));
      task.promise
        .then((result) => { if (!cancelled) setPreview(result); })
        .catch((error) => {
          if (cancelled || error === CANCELLED) return;
          setPreview({ evidenceId: '', status: 'unavailable', failureCode: '', url: '' });
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    };

    // Без IntersectionObserver (старый движок, jsdom) грузим сразу: лучше
    // лишний запрос, чем пустая колонка.
    if (typeof IntersectionObserver === 'undefined') {
      load();
      return () => { cancelled = true; task?.cancel(); };
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        load();
        observer.disconnect();
      }
    }, { rootMargin: '200px' });

    observer.observe(node);

    return () => {
      cancelled = true;
      observer.disconnect();
      task?.cancel();
    };
  }, [eventId]);

  const handleImageError = () => {
    // Первый отказ браузера: если у нас есть evidenceId, пробуем запросить свежую URL.
    if (retriedRef.current || !preview?.evidenceId) {
      // Уже повторили или evidenceId отсутствует → сразу unavailable.
      setPreview((prev) => ({ ...prev, status: 'unavailable', failureCode: '' }));
      return;
    }

    retriedRef.current = true;
    getEvidenceUrl(preview.evidenceId)
      .then((link) => {
        const newUrl = link?.url ?? '';
        // Пункт 2: если бэкенд вернул ту же ссылку, браузер её не перезапросит.
        // Сразу переводим в unavailable.
        if (newUrl === preview.url) {
          setPreview((prev) => ({ ...prev, status: 'unavailable', failureCode: '' }));
        } else if (newUrl) {
          // Новая URL отличается — пробуем загрузить заново.
          setPreview((prev) => ({ ...prev, url: newUrl }));
        } else {
          // Пустая URL в ответе → unavailable.
          setPreview((prev) => ({ ...prev, status: 'unavailable', failureCode: '' }));
        }
      })
      .catch(() => {
        // Ошибка при запросе свежей URL → unavailable.
        setPreview((prev) => ({ ...prev, status: 'unavailable', failureCode: '' }));
      });
  };

  const state = describeEvidenceState(preview || {});

  const renderInner = () => {
    if (!preview || loading) return <span className="vio-thumb-text">Загрузка…</span>;
    if (state.kind === 'ready' && preview.url) {
      return (
        <img
          className="vio-thumb-image"
          src={preview.url}
          alt="Кадр нарушения"
          onError={handleImageError}
        />
      );
    }
    return <span className="vio-thumb-text">{state.text}</span>;
  };

  const clickable = state.kind === 'ready' && Boolean(preview?.url) && Boolean(onOpen);

  return (
    <div
      ref={holderRef}
      className={`vio-thumb${clickable ? ' vio-thumb--clickable' : ''}`}
      onClick={clickable ? (event) => { event.stopPropagation(); onOpen({ url: preview.url, caption }); } : undefined}
    >
      {renderInner()}
    </div>
  );
};

export default ViolationEvidenceThumbnail;
